import React, { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { downloadFile } from "../lib/api";
import {
  ShieldCheck,
  KeyRound,
  Save,
  Download,
  Database,
  Smartphone,
  Bluetooth,
  Bell,
} from "lucide-react";
import { toast } from "sonner";
import {
  canInstall,
  promptInstall,
  getReminderTime,
  setReminderTime,
  requestNotificationPermission,
} from "../lib/sw-register";
import { pairPrinter, getSavedPrinter } from "../lib/bluetoothPrinter";

export default function Settings() {
  const { pins, updatePins } = useAuth();
  const [ownerNew, setOwnerNew] = useState("");
  const [ownerConfirm, setOwnerConfirm] = useState("");
  const [staffNew, setStaffNew] = useState("");
  const [staffConfirm, setStaffConfirm] = useState("");
  const [installable, setInstallable] = useState(canInstall());
  const [reminder, setReminder] = useState(getReminderTime());
  const [printerName, setPrinterName] = useState(getSavedPrinter());
  const [notifPerm, setNotifPerm] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported"
  );

  useEffect(() => {
    const h = () => setInstallable(true);
    window.addEventListener("eeman-can-install", h);
    return () => window.removeEventListener("eeman-can-install", h);
  }, []);

  const isValidPin = (p) => /^\d{4,6}$/.test(String(p || "").trim());

  const saveOwner = () => {
    if (!isValidPin(ownerNew)) {
      toast.error("Owner PIN must be 4-6 digits");
      return;
    }
    if (ownerNew !== ownerConfirm) {
      toast.error("Owner PIN confirmation mismatch");
      return;
    }
    updatePins({ owner: ownerNew });
    setOwnerNew("");
    setOwnerConfirm("");
    toast.success("Owner PIN updated");
  };

  const saveStaff = () => {
    if (!isValidPin(staffNew)) {
      toast.error("Staff PIN must be 4-6 digits");
      return;
    }
    if (staffNew !== staffConfirm) {
      toast.error("Staff PIN confirmation mismatch");
      return;
    }
    if (staffNew === pins.owner) {
      toast.error("Staff PIN cannot equal Owner PIN");
      return;
    }
    updatePins({ staff: staffNew });
    setStaffNew("");
    setStaffConfirm("");
    toast.success("Staff PIN updated");
  };

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto page-enter" data-testid="settings-page">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Admin</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter mt-1">
          Settings
        </h1>
        <p className="text-sm text-zinc-600 mt-2">
          Manage device PINs. Current Owner PIN required to change.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Owner */}
        <div className="border border-zinc-200 bg-white p-5" data-testid="settings-owner-card">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck className="h-5 w-5 text-sky-600" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                Owner PIN
              </div>
              <div className="font-display text-lg font-semibold">Full Access</div>
            </div>
          </div>
          <div className="mb-3">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
              Current
            </div>
            <div className="font-mono-num text-xl tracking-[0.3em]">
              {pins.owner.replace(/./g, "•")}{" "}
              <span className="text-zinc-400 text-xs ml-2">{pins.owner.length} digits</span>
            </div>
          </div>
          <div className="space-y-2">
            <input
              type="password"
              inputMode="numeric"
              placeholder="New PIN (4-6 digits)"
              value={ownerNew}
              onChange={(e) => setOwnerNew(e.target.value.replace(/\D/g, "").slice(0, 6))}
              data-testid="settings-owner-new"
              className="w-full border border-zinc-300 px-3 py-2 font-mono-num tracking-[0.3em] focus:outline-none focus:ring-2 focus:ring-sky-600"
            />
            <input
              type="password"
              inputMode="numeric"
              placeholder="Confirm new PIN"
              value={ownerConfirm}
              onChange={(e) => setOwnerConfirm(e.target.value.replace(/\D/g, "").slice(0, 6))}
              data-testid="settings-owner-confirm"
              className="w-full border border-zinc-300 px-3 py-2 font-mono-num tracking-[0.3em] focus:outline-none focus:ring-2 focus:ring-sky-600"
            />
            <button
              onClick={saveOwner}
              data-testid="settings-owner-save"
              className="w-full bg-sky-600 text-white py-2 text-xs uppercase tracking-wider hover:bg-sky-700 flex items-center justify-center gap-2"
            >
              <Save className="h-3 w-3" />
              Update Owner PIN
            </button>
          </div>
        </div>

        {/* Staff */}
        <div className="border border-zinc-200 bg-white p-5" data-testid="settings-staff-card">
          <div className="flex items-center gap-2 mb-4">
            <KeyRound className="h-5 w-5 text-zinc-900" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                Staff PIN
              </div>
              <div className="font-display text-lg font-semibold">POS + Wholesale</div>
            </div>
          </div>
          <div className="mb-3">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
              Current
            </div>
            <div className="font-mono-num text-xl tracking-[0.3em]">
              {pins.staff.replace(/./g, "•")}{" "}
              <span className="text-zinc-400 text-xs ml-2">{pins.staff.length} digits</span>
            </div>
          </div>
          <div className="space-y-2">
            <input
              type="password"
              inputMode="numeric"
              placeholder="New PIN (4-6 digits)"
              value={staffNew}
              onChange={(e) => setStaffNew(e.target.value.replace(/\D/g, "").slice(0, 6))}
              data-testid="settings-staff-new"
              className="w-full border border-zinc-300 px-3 py-2 font-mono-num tracking-[0.3em] focus:outline-none focus:ring-2 focus:ring-sky-600"
            />
            <input
              type="password"
              inputMode="numeric"
              placeholder="Confirm new PIN"
              value={staffConfirm}
              onChange={(e) => setStaffConfirm(e.target.value.replace(/\D/g, "").slice(0, 6))}
              data-testid="settings-staff-confirm"
              className="w-full border border-zinc-300 px-3 py-2 font-mono-num tracking-[0.3em] focus:outline-none focus:ring-2 focus:ring-sky-600"
            />
            <button
              onClick={saveStaff}
              data-testid="settings-staff-save"
              className="w-full bg-zinc-900 text-white py-2 text-xs uppercase tracking-wider hover:bg-zinc-800 flex items-center justify-center gap-2"
            >
              <Save className="h-3 w-3" />
              Update Staff PIN
            </button>
          </div>
        </div>
      </div>

      <div className="border border-amber-200 bg-amber-50 p-4 mt-5 text-xs text-amber-900" data-testid="settings-help">
        <div className="font-semibold uppercase tracking-wider mb-1">Tip</div>
        PINs are stored on this device only. If you forget the Owner PIN, clear the browser&apos;s
        local storage to reset to defaults (Owner <b>9999</b> · Staff <b>1234</b>).
      </div>

      {/* Backup section */}
      <div className="mt-8" data-testid="backup-section">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 flex items-center gap-2">
          <Database className="h-3 w-3" />
          Data Backup
        </div>
        <div className="border border-zinc-200 bg-white p-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[280px]">
              <div className="font-display text-lg font-semibold">Full Database Backup</div>
              <p className="text-xs text-zinc-600 mt-1 max-w-md">
                Download a ZIP containing every collection (products, sales, purchases,
                customers, expenses, payments, rates) as CSV files. Save it locally as
                your own offline backup.
              </p>
            </div>
            <button
              data-testid="backup-download-btn"
              className="bg-sky-600 text-white px-5 py-3 text-xs uppercase tracking-wider hover:bg-sky-700 flex items-center gap-2"
              onClick={() => {
                toast.success("Preparing backup ZIP…");
                downloadFile(
                  "/backup/full",
                  `eeman-meats-backup-${new Date().toISOString().slice(0, 10)}.zip`
                ).catch(() => toast.error("Backup download failed"));
              }}
            >
              <Download className="h-4 w-4" />
              Download Backup
            </button>
          </div>
          <div className="mt-4 pt-4 border-t border-zinc-200 text-[11px] text-zinc-600">
            <b className="text-zinc-900">Tip:</b> Save the ZIP weekly to a cloud drive (Google
            Drive, OneDrive, etc.) or USB stick. The platform also keeps automatic
            server-side backups.
          </div>
        </div>
      </div>

      {/* Install as App */}
      <div className="mt-8" data-testid="install-section">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 flex items-center gap-2">
          <Smartphone className="h-3 w-3" /> Install as App
        </div>
        <div className="border border-zinc-200 bg-white p-5 flex flex-wrap items-center justify-between gap-3">
          <div className="flex-1 min-w-[280px]">
            <div className="font-display text-lg font-semibold">Add to home screen</div>
            <p className="text-xs text-zinc-600 mt-1">
              Works offline. Loads instantly. Looks like a native app.
              {installable ? "" : " (On iPhone: Safari → Share → Add to Home Screen)"}
            </p>
          </div>
          <button
            onClick={async () => {
              const ok = await promptInstall();
              if (ok) toast.success("App installed");
              else toast.info("Open browser menu → Add to Home Screen");
            }}
            disabled={!installable}
            data-testid="install-btn"
            className="bg-sky-600 text-white px-5 py-3 text-xs uppercase tracking-wider hover:bg-sky-700 disabled:bg-zinc-300 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Smartphone className="h-4 w-4" />
            {installable ? "Install Now" : "Use Browser Menu"}
          </button>
        </div>
      </div>

      {/* Bluetooth Printer */}
      <div className="mt-8" data-testid="bt-section">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 flex items-center gap-2">
          <Bluetooth className="h-3 w-3" /> Thermal Printer
        </div>
        <div className="border border-zinc-200 bg-white p-5 flex flex-wrap items-center justify-between gap-3">
          <div className="flex-1 min-w-[280px]">
            <div className="font-display text-lg font-semibold">Bluetooth Printer Pairing</div>
            <p className="text-xs text-zinc-600 mt-1">
              Pair your 58mm/80mm ESC/POS thermal printer once. Then &quot;Print to Bluetooth&quot;
              button on every bill sends instantly.
            </p>
            {printerName && (
              <div className="text-xs text-emerald-700 mt-2">
                ✓ Saved: <b>{printerName}</b>
              </div>
            )}
          </div>
          <button
            onClick={async () => {
              try {
                const name = await pairPrinter();
                setPrinterName(name);
                toast.success(`Paired with ${name}`);
              } catch (e) {
                toast.error(e.message || "Pairing failed");
              }
            }}
            data-testid="bt-pair-btn"
            className="bg-sky-600 text-white px-5 py-3 text-xs uppercase tracking-wider hover:bg-sky-700 flex items-center gap-2"
          >
            <Bluetooth className="h-4 w-4" />
            {printerName ? "Re-pair Printer" : "Pair Printer"}
          </button>
        </div>
      </div>

      {/* Daily reminder */}
      <div className="mt-8" data-testid="reminder-section">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 flex items-center gap-2">
          <Bell className="h-3 w-3" /> Daily Reminder
        </div>
        <div className="border border-zinc-200 bg-white p-5 flex flex-wrap items-center justify-between gap-3">
          <div className="flex-1 min-w-[260px]">
            <div className="font-display text-lg font-semibold">Notify me daily at</div>
            <p className="text-xs text-zinc-600 mt-1">
              Local browser notification — reminds you to review P&amp;L and set tomorrow&apos;s
              rates. Permission: <b>{notifPerm}</b>.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="time"
              value={reminder}
              onChange={(e) => {
                setReminder(e.target.value);
                setReminderTime(e.target.value);
              }}
              data-testid="reminder-time"
              className="border border-zinc-300 px-3 py-2 text-sm font-mono-num focus:outline-none focus:ring-2 focus:ring-sky-600"
            />
            <button
              onClick={async () => {
                const res = await requestNotificationPermission();
                setNotifPerm(res);
                if (res === "granted") toast.success("Notifications enabled");
                else toast.error("Notification permission denied");
              }}
              data-testid="reminder-enable-btn"
              className="bg-sky-600 text-white px-4 py-2 text-xs uppercase tracking-wider hover:bg-sky-700"
            >
              {notifPerm === "granted" ? "Enabled ✓" : "Enable"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
